import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { Button } from '../components/Button';
import { SRErrorNotice } from '../components/SRErrorNotice';
import { ApiService, SRConfigStatus, SRConnectionStatus } from '../services/apiService';
import {
  oauthCallbackToSrError,
  SRErrorDisplay,
  toSrErrorDisplay,
} from '../utils/srErrors';

function formatSubscribedAt(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export const SRIntegrationPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connection, setConnection] = useState<SRConnectionStatus | null>(null);
  const [configStatus, setConfigStatus] = useState<SRConfigStatus | null>(null);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [hubLoading, setHubLoading] = useState(false);
  const [connectionError, setConnectionError] = useState<SRErrorDisplay | null>(null);
  const [connectionNotice, setConnectionNotice] = useState<string | null>(null);

  const loadConnection = useCallback(async () => {
    setConnectionLoading(true);
    setConnectionError(null);
    try {
      const status = await ApiService.getSRConnection();
      setConnection(status);
    } catch (err) {
      setConnectionError(
        toSrErrorDisplay(err, {
          code: 'SR-001',
          message: 'Unable to load SR connection status. Please try again.',
        }),
      );
    } finally {
      setConnectionLoading(false);
    }
  }, []);

  const loadConfigStatus = useCallback(async () => {
    setConfigLoading(true);
    try {
      const status = await ApiService.getSRConfigStatus();
      setConfigStatus(status);
    } catch (err) {
      setConnectionError(
        toSrErrorDisplay(err, {
          code: 'SR-004',
          message: 'Unable to load SR configuration status. Please try again.',
        }),
      );
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnection();
    loadConfigStatus();
  }, [loadConnection, loadConfigStatus]);

  useEffect(() => {
    const srConnected = searchParams.get('sr_connected');
    const srError = searchParams.get('sr_error');
    if (srConnected === '1') {
      setConnectionNotice('Successfully connected to Specialist Referrals.');
      setSearchParams({}, { replace: true });
      loadConnection();
    } else if (srError) {
      setConnectionError(oauthCallbackToSrError(srError));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, loadConnection]);

  const missingOAuthCount = configStatus?.missing_for_oauth.length ?? 0;
  const missingWebhookCount = configStatus?.missing_for_webhooks.length ?? 0;
  const serverConfigured = configStatus?.configured ?? false;

  // AXISREF441: group the checklist — SR v2 integration settings first
  // (oauth/jwt/webhook), AI document extraction last in its own group.
  const srSettings = (configStatus?.settings ?? []).filter(
    (s) => s.group !== 'extraction',
  );
  const extractionSettings = (configStatus?.settings ?? []).filter(
    (s) => s.group === 'extraction',
  );

  const handleConnect = async () => {
    setActionLoading(true);
    setConnectionError(null);
    setConnectionNotice(null);
    try {
      const { auth_url } = await ApiService.startSROAuth();
      window.location.href = auth_url;
    } catch (err) {
      setConnectionError(
        toSrErrorDisplay(err, {
          code: 'SR-005',
          message: 'Unable to start SR connection. Please try again.',
        }),
      );
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setActionLoading(true);
    setConnectionError(null);
    setConnectionNotice(null);
    try {
      await ApiService.disconnectSR();
      setConnectionNotice('Disconnected from Specialist Referrals.');
      await loadConnection();
    } catch (err) {
      setConnectionError(
        toSrErrorDisplay(err, {
          code: 'SR-011',
          message: 'Unable to disconnect from SR. Please try again.',
        }),
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleLaunchPortal = async () => {
    setHubLoading(true);
    setConnectionError(null);
    try {
      const { url } = await ApiService.getSRHubLoginLink();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setConnectionError(
        toSrErrorDisplay(err, {
          code: 'SR-015',
          message: 'Unable to open SR portal. Please try again.',
        }),
      );
    } finally {
      setHubLoading(false);
    }
  };

  const isConnected = Boolean(connection?.connected && connection.subscription_active);

  return (
    <>
      <div className="admin-page-header">
        <h2>SR Integration</h2>
        <p className="admin-page-subtitle">
          Connect your practice to Specialist Referrals and launch the SR Referral Hub.
        </p>
      </div>

      {connectionNotice && (
        <div className="admin-notice admin-notice--success">{connectionNotice}</div>
      )}
      {connectionError && (
        <SRErrorNotice code={connectionError.code} message={connectionError.message} />
      )}

      <section className="admin-card">
        <h3>Server configuration</h3>
        <p className="admin-card-description">
          Indicates whether required server settings are present. Values are never shown.
        </p>
        {configLoading ? (
          <p>Checking server configuration…</p>
        ) : configStatus ? (
          <>
            {!serverConfigured && (
              <div className="admin-notice admin-notice--warning" role="status">
                {missingOAuthCount > 0 && (
                  <span>
                    {missingOAuthCount} setting{missingOAuthCount === 1 ? '' : 's'} missing for OAuth
                    {missingWebhookCount > 0 ? '; ' : '.'}
                  </span>
                )}
                {missingWebhookCount > 0 && (
                  <span>
                    {missingWebhookCount} setting{missingWebhookCount === 1 ? '' : 's'} missing for
                    webhook authentication.
                  </span>
                )}
              </div>
            )}
            {serverConfigured && (
              <div className="admin-notice admin-notice--success" role="status">
                All required server settings are present.
              </div>
            )}
            {[
              { title: null as string | null, items: srSettings },
              {
                title: 'AI document extraction (Azure)',
                items: extractionSettings,
              },
            ].map((group, groupIdx) =>
              group.items.length === 0 ? null : (
                <div key={groupIdx}>
                  {group.title && (
                    <p
                      className="admin-card-hint"
                      style={{ margin: '16px 0 0', fontWeight: 600 }}
                    >
                      {group.title}
                      {!configStatus.extraction_configured && ' — incomplete'}
                    </p>
                  )}
                  <ul
                    className="admin-config-checklist"
                    style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}
                  >
                    {group.items.map((setting) => (
                      <li
                        key={setting.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 0',
                          borderBottom: '1px solid var(--color-border, #e5e7eb)',
                        }}
                      >
                        <FeatherIcon
                          icon={setting.present ? 'check-circle' : 'x-circle'}
                          size={16}
                          style={{ color: setting.present ? '#16a34a' : '#dc2626', flexShrink: 0 }}
                        />
                        <span>{setting.label}</span>
                        <span
                          style={{
                            marginLeft: 'auto',
                            fontSize: '0.85rem',
                            color: 'var(--color-text-muted, #6b7280)',
                          }}
                        >
                          {setting.present ? 'Present' : 'Missing'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ),
            )}
          </>
        ) : null}
      </section>

      <section className="admin-card">
        <h3>Connection</h3>
        {connectionLoading ? (
          <p>Loading connection status…</p>
        ) : (
          <>
            <div className="admin-status-grid">
              <div>
                <span className="admin-status-label">Status</span>
                <div className="admin-status-value">
                  <FeatherIcon icon={isConnected ? 'check-circle' : 'alert-circle'} size={16} />
                  {isConnected
                    ? 'Connected'
                    : connection?.connected
                      ? 'Connected — subscription inactive'
                      : 'Not connected'}
                </div>
              </div>
              {connection?.sr_user_id && (
                <div>
                  <span className="admin-status-label">SR user ID</span>
                  <div className="admin-status-value">{connection.sr_user_id}</div>
                </div>
              )}
              {connection?.subscribed_at && (
                <div>
                  <span className="admin-status-label">Subscribed</span>
                  <div className="admin-status-value">
                    {formatSubscribedAt(connection.subscribed_at)}
                  </div>
                </div>
              )}
            </div>

            <div className="admin-card-actions">
              {connection?.connected ? (
                <Button
                  variant="secondary"
                  onClick={handleDisconnect}
                  disabled={actionLoading}
                  isLoading={actionLoading}
                >
                  Disconnect from Specialist Referrals
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleConnect}
                  disabled={actionLoading || !serverConfigured}
                  isLoading={actionLoading}
                >
                  Connect to Specialist Referrals
                </Button>
              )}
            </div>
            {!serverConfigured && !connection?.connected && (
              <p className="admin-card-hint">
                Configure missing server settings before connecting to Specialist Referrals.
              </p>
            )}
          </>
        )}
      </section>

      <section className="admin-card">
        <h3>Launch SR Portal</h3>
        <p className="admin-card-description">
          Opens the SR Referral Hub in a new tab using a one-time SSO link (valid for 30 minutes).
          The link is generated on demand when you click the button.
        </p>
        <Button
          variant="primary"
          onClick={handleLaunchPortal}
          disabled={!isConnected || hubLoading}
          isLoading={hubLoading}
        >
          Launch SR Portal
        </Button>
        {!isConnected && (
          <p className="admin-card-hint">
            Connect SR Integration first before launching the portal.
          </p>
        )}
      </section>

      <p className="admin-card-hint">
        Manage SR referral workflow on the{' '}
        <Link to="/admin/sr-queue">SR Referrals</Link> page.
      </p>
    </>
  );
};
